import type {
  Database,
  Company,
  User,
  Project,
  ContractItem,
  InterimApplication,
  Tender,
  RetentionLedger,
  Task,
  TaskComment,
  Notification,
  ShareToken,
  RateLibrary,
  MainContractor,
  Contact,
  EstimatingTeamMember,
  Supplier,
  TermsTemplate,
  RateLibraryItem,
  RICSBenchmark,
  CommercialHealthMetric,
  Employer,
  ClientEmployerRelationship,
  EntitlementAlert,
  ActiveDispute,
  ClientRequest,
  PendingDocumentUpload,
} from '../../../types/database';
import type { Claim, ClaimCorrespondence, ClaimSummaryMetrics, ClaimFilters, ClaimDocumentFolderKey } from '../../../types/claims';
import type { Document } from '../../../types/documents';
import type {
  Activity,
  ActivityFilter,
  ActivityStats,
  CreatePhoneCallData,
  CreateMeetingData,
  CreateEmailData,
  CreateNoteData,
  CreateCommentData,
} from '../../../types/activity';
import { ProjectFoldersService } from '../../ProjectFoldersService';
import { projectId, publicAnonKey } from '../../../utils/supabase/info';
import { geocodeAddress } from '../../../utils/geocoding';
import type { Constructor } from './mixins';

export const withClaims = <TBase extends Constructor<any>>(Base: TBase) => class Claims extends Base {
  // CLAIMS & DISPUTES
  // ============================================================================

  private resolveClaimActor(identifier?: string | null) {
    if (!identifier) return null;

    const byId = this.getUser(identifier);
    if (byId) return byId;

    return this.getUsers().find((user: User) => user.email === identifier) || null;
  }

  private getDefaultClaimFolderKey(claim: Pick<Claim, 'status' | 'type'>): ClaimDocumentFolderKey {
    if (claim.status === 'in-adjudication' || claim.type === 'adjudication') {
      return 'noticesReferrals';
    }

    return 'claims';
  }

  private logClaimActivity(
    claim: Claim,
    activity: {
      type: 'project_update' | 'status_update' | 'document_upload' | 'note';
      title: string;
      description?: string;
      userId?: string;
      userName?: string;
      userEmail?: string;
      status_from?: string;
      status_to?: string;
      document_category?: string;
    }
  ) {
    this.logActivity({
      type: activity.type,
      title: activity.title,
      description: activity.description,
      projectId: claim.projectId,
      projectName: claim.projectName,
      clientId: claim.clientId,
      clientName: claim.clientName,
      userId: activity.userId,
      userName: activity.userName,
      userEmail: activity.userEmail,
      status_from: activity.status_from,
      status_to: activity.status_to,
      document_category: activity.document_category || claim.documentFolderKey,
      amount: claim.claimedValue,
      currency: 'GBP',
      is_internal: true,
    });
  }

  public getClaims(): Claim[] {
    return Object.values(this.db.claims);
  }

  public getClaim(id: string): Claim | undefined {
    return this.db.claims[id];
  }

  public getClaimsByProject(projectId: string): Claim[] {
    return this.getClaims().filter(c => c.projectId === projectId);
  }

  public getClaimsByClient(clientId: string): Claim[] {
    return this.getClaims().filter(c => c.clientId === clientId);
  }

  public getClaimsByStatus(status: string): Claim[] {
    return this.getClaims().filter(c => c.status === status);
  }

  public getFilteredClaims(filters: ClaimFilters): Claim[] {
    let claims = this.getClaims();

    if (filters.projectId) {
      claims = claims.filter(c => c.projectId === filters.projectId);
    }

    if (filters.clientId) {
      claims = claims.filter(c => c.clientId === filters.clientId);
    }

    if (filters.status && filters.status.length > 0) {
      claims = claims.filter(c => filters.status!.includes(c.status));
    }

    if (filters.type && filters.type.length > 0) {
      claims = claims.filter(c => filters.type!.includes(c.type));
    }

    if (filters.dateFrom) {
      claims = claims.filter(c => c.submissionDate && c.submissionDate >= filters.dateFrom!);
    }

    if (filters.dateTo) {
      claims = claims.filter(c => c.submissionDate && c.submissionDate <= filters.dateTo!);
    }

    if (filters.searchTerm) {
      const term = filters.searchTerm.toLowerCase();
      claims = claims.filter(c => 
        c.title.toLowerCase().includes(term) ||
        c.description.toLowerCase().includes(term) ||
        c.claimNumber.toLowerCase().includes(term) ||
        c.claimant.toLowerCase().includes(term) ||
        c.respondent.toLowerCase().includes(term)
      );
    }

    return claims;
  }

  public getClaimSummaryMetrics(filters?: ClaimFilters): ClaimSummaryMetrics {
    const claims = filters ? this.getFilteredClaims(filters) : this.getClaims();
    
    const activeClaims = claims.filter(c => 
      !['approved', 'rejected', 'withdrawn', 'settled'].includes(c.status)
    );

    const resolvedClaims = claims.filter(c => 
      ['approved', 'settled'].includes(c.status)
    );

    const pendingReview = claims.filter(c => c.status === 'under-review');
    const inAdjudication = claims.filter(c => c.status === 'in-adjudication');

    const totalValue = claims.reduce((sum, c) => sum + c.claimedValue, 0);
    const approvedValue = claims.reduce((sum, c) => sum + (c.approvedValue || 0), 0);
    const pendingValue = activeClaims.reduce((sum, c) => sum + c.claimedValue, 0);

    // Calculate average resolution time
    const resolvedWithDates = resolvedClaims.filter(c => c.submissionDate && c.resolvedDate);
    let averageResolutionDays: number | undefined;
    
    if (resolvedWithDates.length > 0) {
      const totalDays = resolvedWithDates.reduce((sum, c) => {
        const submitted = this.parseUKDate(c.submissionDate!);
        const resolved = this.parseUKDate(c.resolvedDate!);
        const days = Math.floor((resolved.getTime() - submitted.getTime()) / (1000 * 60 * 60 * 24));
        return sum + days;
      }, 0);
      averageResolutionDays = Math.round(totalDays / resolvedWithDates.length);
    }

    return {
      activeClaims: activeClaims.length,
      totalValue,
      approvedValue,
      pendingValue,
      resolvedCount: resolvedClaims.length,
      pendingReviewCount: pendingReview.length,
      inAdjudicationCount: inAdjudication.length,
      averageResolutionDays,
    };
  }

  public createClaim(claimData: Omit<Claim, 'id' | 'createdAt' | 'updatedAt'>): Claim {
    // Generate claim number if not provided
    const year = new Date().getFullYear();
    const existingClaims = this.getClaims().filter(c => c.claimNumber.includes(year.toString()));
    const nextNumber = existingClaims.length + 1;
    const claimNumber = claimData.claimNumber || `CLM-${year}-${String(nextNumber).padStart(3, '0')}`;

    const newClaim: Claim = {
      ...claimData,
      claimNumber,
      id: this.generateId(),
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      documentFolderKey: claimData.documentFolderKey || this.getDefaultClaimFolderKey(claimData),
    };

    this.db.claims[newClaim.id] = newClaim;
    this.saveToStorage();
    const actor = this.resolveClaimActor(claimData.createdBy);
    this.logClaimActivity(newClaim, {
      type: 'project_update',
      title: `Claim created: ${newClaim.claimNumber}`,
      description: `${newClaim.title} recorded as ${newClaim.type} for ${newClaim.claimedValue.toLocaleString('en-GB', { style: 'currency', currency: 'GBP' })}.`,
      userId: actor?.id,
      userName: actor?.name || claimData.createdBy,
      userEmail: actor?.email || claimData.createdBy,
      document_category: newClaim.documentFolderKey,
    });
    this.notifyListeners();
    return newClaim;
  }

  public updateClaim(id: string, updates: Partial<Claim>): Claim | undefined {
    const claim = this.db.claims[id];
    if (!claim) return undefined;

    const updated: Claim = {
      ...claim,
      ...updates,
      id,
      updatedAt: new Date().toISOString(),
      documentFolderKey: updates.documentFolderKey || claim.documentFolderKey || this.getDefaultClaimFolderKey({
        status: updates.status || claim.status,
        type: updates.type || claim.type,
      }),
    };

    if (
      !updates.documentFolderKey &&
      claim.status !== 'in-adjudication' &&
      updates.status === 'in-adjudication'
    ) {
      updated.documentFolderKey = 'noticesReferrals';
    }

    this.db.claims[id] = updated;
    this.saveToStorage();

    const actor = this.resolveClaimActor(updates.assignedTo || claim.assignedTo || claim.createdBy);

    if (updates.status && updates.status !== claim.status) {
      this.logClaimActivity(updated, {
        type: 'status_update',
        title: `Claim status updated: ${updated.claimNumber}`,
        description: `${updated.title} moved from ${claim.status} to ${updates.status}.`,
        userId: actor?.id,
        userName: actor?.name || updates.assignedTo || claim.assignedTo || claim.createdBy,
        userEmail: actor?.email || updates.assignedTo || claim.assignedTo || claim.createdBy,
        status_from: claim.status,
        status_to: updates.status,
        document_category: updated.documentFolderKey,
      });
    }

    if (updates.supportingDocuments && updates.supportingDocuments !== claim.supportingDocuments) {
      this.logClaimActivity(updated, {
        type: 'document_upload',
        title: `Claim documents updated: ${updated.claimNumber}`,
        description: `${updated.supportingDocuments?.length || 0} supporting document${(updated.supportingDocuments?.length || 0) === 1 ? '' : 's'} linked to ${updated.title}.`,
        userId: actor?.id,
        userName: actor?.name || updates.assignedTo || claim.assignedTo || claim.createdBy,
        userEmail: actor?.email || updates.assignedTo || claim.assignedTo || claim.createdBy,
        document_category: updated.documentFolderKey,
      });
    }

    this.notifyListeners();
    return updated;
  }

  public deleteClaim(id: string): boolean {
    const claim = this.db.claims[id];
    if (!claim) return false;
    delete this.db.claims[id];
    
    // Also delete related correspondence
    const correspondence = this.getClaimCorrespondence(id);
    correspondence.forEach(c => delete this.db.claimCorrespondence[c.id]);

    this.saveToStorage();
    const actor = this.resolveClaimActor(claim.assignedTo || claim.createdBy);
    this.logClaimActivity(claim, {
      type: 'project_update',
      title: `Claim deleted: ${claim.claimNumber}`,
      description: `${claim.title} and ${correspondence.length} correspondence item${correspondence.length === 1 ? '' : 's'} were removed.`,
      userId: actor?.id,
      userName: actor?.name || claim.assignedTo || claim.createdBy,
      userEmail: actor?.email || claim.assignedTo || claim.createdBy,
      document_category: claim.documentFolderKey,
    });
    this.notifyListeners();
    return true;
  }

  // Claim Correspondence

  public getClaimCorrespondence(claimId: string): ClaimCorrespondence[] {
    return (Object.values(this.db.claimCorrespondence) as ClaimCorrespondence[])
      .filter((c: ClaimCorrespondence) => c.claimId === claimId)
      .sort((a: ClaimCorrespondence, b: ClaimCorrespondence) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  }

  public createClaimCorrespondence(
    data: Omit<ClaimCorrespondence, 'id' | 'createdAt'>
  ): ClaimCorrespondence {
    const correspondence: ClaimCorrespondence = {
      ...data,
      id: this.generateId(),
      createdAt: new Date().toISOString(),
    };

    this.db.claimCorrespondence[correspondence.id] = correspondence;
    this.saveToStorage();
    const claim = this.getClaim(data.claimId);
    if (claim) {
      const actor = this.resolveClaimActor(data.createdBy);
      this.logClaimActivity(claim, {
        type: 'note',
        title: `Claim correspondence: ${claim.claimNumber}`,
        description: `${data.subject} from ${data.from} to ${data.to}.`,
        userId: actor?.id,
        userName: actor?.name || data.createdBy,
        userEmail: actor?.email || data.createdBy,
        document_category: claim.documentFolderKey,
      });
    }
    this.notifyListeners();
    return correspondence;
  }

  public deleteClaimCorrespondence(id: string): boolean {
    if (!this.db.claimCorrespondence[id]) return false;
    delete this.db.claimCorrespondence[id];
    this.saveToStorage();
    this.notifyListeners();
    return true;
  }

  // Helper method to parse UK dates (DD-MM-YYYY)
  private parseUKDate(dateStr: string): Date {
    const [day, month, year] = dateStr.split('-').map(Number);
    return new Date(year, month - 1, day);
  }

  // ============================================================================
};
